import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook to navigate back to CompanyDashboardPart2 when backspace is pressed
 * Only triggers when not in an input field, textarea, or contenteditable element
 * 
 * @param {boolean} enabled - Whether the hook is enabled (default: true)
 * @param {string} targetPath - Target path to navigate to (default: '/company/dashboard-part2')
 */
export const useBackspaceNavigation = (enabled = true, targetPath = '/company/dashboard-part2') => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      // Only trigger if Backspace is pressed
      if (e.key !== 'Backspace') return;

      // Don't trigger if user is typing in an input field, textarea, or contenteditable element
      const target = e.target;
      const isInputField = target.matches('input, textarea, [contenteditable="true"]');
      
      // Don't trigger if user is in a select dropdown or button
      const isInteractiveElement = target.matches('select, button, a');
      
      // Don't trigger if user is in a modal or dialog
      const isInModal = target.closest('[role="dialog"]') || target.closest('.modal') || target.closest('[data-modal]');
      
      if (isInputField || isInteractiveElement || isInModal) {
        return;
      }

      // Prevent default backspace behavior (going back in browser history)
      e.preventDefault();
      
      // Navigate to CompanyDashboardPart2
      navigate(targetPath);
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, navigate, targetPath]);
};

